// Notification preferences. Backend lands in B6/B7 — until then we persist
// preferences locally per profile so the demo feels real.

import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  BackLink,
  Card,
  CardTitle,
  ContentMaxWidth,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  Row,
  Stack,
} from '../../layout/styledComponents'
import { usePatient } from '../../context/PatientContext'

const STORAGE_KEY = 'patientPortal.mock.notificationPrefs.v1'

const Toggle = styled.button`
  appearance: none;
  border: none;
  background: ${(p) => (p.$on ? p.theme?.color?.accent || '#6B9080' : '#D5CCBC')};
  width: 42px;
  height: 24px;
  border-radius: 999px;
  position: relative;
  cursor: pointer;
  transition: background 200ms ease-out;
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${(p) => (p.$on ? '20px' : '2px')};
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: left 200ms ease-out;
  }
`

const Item = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
  & + & {
    border-top: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  }
`

const DEFAULT_PREFS = {
  medicineReminders: true,
  sessionReminders: true,
  refillReminders: true,
  practitionerMessages: true,
  marketingNudges: false,
  weeklyDigest: true,
}

const LABELS = {
  medicineReminders: 'Medicine reminders',
  sessionReminders: 'Session reminders',
  refillReminders: 'Refill reminders',
  practitionerMessages: 'Practitioner messages',
  marketingNudges: 'Marketing nudges',
  weeklyDigest: 'Weekly progress digest',
}

const SUBLABELS = {
  medicineReminders: 'Pings before each scheduled dose.',
  sessionReminders: 'Email + push at the start of session day.',
  refillReminders: 'When a medicine is running low.',
  practitionerMessages: 'Push when a practitioner replies.',
  marketingNudges: 'Tips, offers, new features.',
  weeklyDigest: 'Your week in one email — Sundays.',
}

function readPrefs(profileId) {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
    return { ...DEFAULT_PREFS, ...(all[profileId] || {}) }
  } catch (e) {
    return DEFAULT_PREFS
  }
}

function writePrefs(profileId, prefs) {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
    all[profileId] = prefs
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all))
  } catch (e) {
    /* ignore */
  }
}

export default function NotificationPrefsPage() {
  const { currentProfile } = usePatient()
  const [prefs, setPrefs] = useState(DEFAULT_PREFS)

  useEffect(() => {
    if (!currentProfile) return
    setPrefs(readPrefs(currentProfile.id))
  }, [currentProfile])

  function toggle(key) {
    setPrefs((prev) => {
      const next = { ...prev, [key]: !prev[key] }
      if (currentProfile) writePrefs(currentProfile.id, next)
      return next
    })
  }

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <BackLink to="/patient-home/profile">Back to Profile</BackLink>
        <div>
          <Pill>Profile</Pill>
          <PageHeading style={{ marginTop: 12 }}>Notification preferences</PageHeading>
          <PageSubheading>
            Per-profile — adjusting here doesn&apos;t affect other profiles you manage.
          </PageSubheading>
        </div>

        <Card>
          {Object.keys(LABELS).map((key) => (
            <Item key={key}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{LABELS[key]}</div>
                <Muted>{SUBLABELS[key]}</Muted>
              </div>
              <Toggle $on={!!prefs[key]} onClick={() => toggle(key)} aria-label={LABELS[key]} />
            </Item>
          ))}
        </Card>
      </Stack>
    </ContentMaxWidth>
  )
}
